import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Checkbox,
  Tooltip,
  IconButton,
  FormControlLabel,
  Stack,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ExpandMore, Person, AccessTime, AttachMoney, CalendarMonth, CheckCircle, Cancel, Edit, Group, FilterList, ExpandLess, Settings, MoneyOff, Receipt, FactCheck as FactCheckIcon, PhotoCamera as PhotoIcon, InfoOutlined } from '@mui/icons-material';
import { getEmployees, markEntryPaid, markEntriesPaid, markEntryInvoiced, updateManagerNotes, getStatsSummary, getTimeEntries, getShiftSettings, getPayPeriodPresets, bulkApproveEntries, getCompanies, getAffiliates, getDepartments, updateTimeEntry, generateInvoicesFromEntries, getEntryPunches, getPunchPhotoBlob, getMyManagedEmployees } from '../api';
import api from '../api';
import { formatTimeDisplay } from '../utils/formatTime';
import { formatCurrency, formatPay, formatHours } from '../utils/formatUtils';
import { buildPresetsFromApi } from '../utils/payPeriodUtils';
import { entrySourceLabel, punchLoadErrorMessage, emptyPunchTrailMessage, offlineTimeNotHonoured } from '../utils/punchDetails';
import { useAuth } from '../context/AuthContext';
import { usePipeline } from '../context/PipelineContext';
import ManagerNotesDialog from './dialogs/ManagerNotesDialog';
import StatusChip from './StatusChip';
import HoursSettingsDialog from './dialogs/HoursSettingsDialog';
import EntryPaymentDialog from './dialogs/EntryPaymentDialog';
import TimeEntryFormDialog from './dialogs/TimeEntryFormDialog';

// True when the server withheld pay on any of these rows (caller lacks View pay rates).
const anyPayHidden = (list) => list.some(e => e.pay_hidden === true);

// Mirrors decisionScopeIds (server/middleware/auth.js): a built-in admin decides
// for everyone; everyone else only for their direct reports, never themselves.
const DECIDES_ALL_ROLES = ['admin'];
const DECIDE_TIP = 'Approve, Unapprove, Mark Paid, Mark Unpaid and the Payment / Invoiced chips work only on entries of your direct reports (never your own). Admins can act on anyone\'s. Ask an admin to assign people to you on Settings > People.';

export default function EmployeeHoursLookup({ onNavigate }) {
  const { user, isAdmin, isManager, hasPermission } = useAuth();
  const { approvalEnabled } = usePipeline();
  const employeeId = user?.employeeId || null;
  // Mobile responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [hoursData, setHoursData] = useState(null);
  const [allEmployeesData, setAllEmployeesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeFormat, setTimeFormat] = useState('12h');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [payPeriodPresets, setPayPeriodPresets] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all'); // 'all' | 'unpaid' | 'partial' | 'paid'
  const [companyFilter, setCompanyFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all'); // 'all' | 'pending' | 'approved'
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Settings
  const defaultSettings = {
    showTotalEntries: true,
    showTotalHours: true,
    showRegularHours: false,
    showOvertimeHours: true,
    showTotalPay: true,
    showAvgRate: false,
    showAvgPerEntry: false,
    showTotalUnpaid: true,
    showWeeklyBreakdown: false,
    showDailyBreakdown: false,
    showEmployeeInfo: false,
    showSummaryStrip: true,
    defaultExpanded: 'entries',
  };

  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('employeeHoursSettings');
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSettings, setTempSettings] = useState(settings);

  const handleOpenSettings = () => {
    setTempSettings(settings);
    setSettingsOpen(true);
  };

  const handleSaveSettings = () => {
    setSettings(tempSettings);
    localStorage.setItem('employeeHoursSettings', JSON.stringify(tempSettings));
    setSettingsOpen(false);
  };

  const handleCancelSettings = () => {
    setSettingsOpen(false);
  };

  // Admin or manager can see employee selector
  const canSelectEmployee = isAdmin || isManager;
  // Mark Paid / Unpaid and the invoiced chip: same rule as the server
  // (Manage invoices or Run payroll; hasPermission is true for program admins)
  const canManagePaid = hasPermission('can_manage_invoices') || hasPermission('can_run_payroll');
  // Can select entries for any bulk action (paid, approve, invoice)
  const canBulkSelect = canManagePaid || hasPermission('can_approve_time') || hasPermission('can_manage_invoices');
  // Admin or manager can edit manager notes
  const canEditManagerNotes = isAdmin || isManager;
  // Admin or manager with can_edit_entry_times can edit the full time entry
  const canEditEntries = isAdmin || hasPermission('can_edit_entry_times');
  // Who the caller may decide for. null = not loaded yet / not needed.
  const decidesAll = DECIDES_ALL_ROLES.includes(user?.role);
  const needsDecisionScope = !decidesAll && (canManagePaid || hasPermission('can_approve_time'));
  const [reviewIds, setReviewIds] = useState(null); // Set of direct-report employee ids (self removed)
  const canDecide = (entry) => decidesAll
    || (reviewIds instanceof Set && reviewIds.has(entry.employee_id) && entry.employee_id !== employeeId);
  const noReports = needsDecisionScope && reviewIds instanceof Set && reviewIds.size === 0;

  // State for editing manager notes dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [managerNotesInput, setManagerNotesInput] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // State for editing a full time entry
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  // Punch evidence behind an entry. This is the screen a manager is on when
  // they ask "is this real?", so the trail belongs here rather than only on
  // the Work Sites admin page.
  const [punchDialog, setPunchDialog] = useState({ open: false, entry: null, punches: [], loading: false, error: null });

  // The kiosk photo. It is fetched as a blob through the API client rather than
  // pointed at with a plain <img src>, because the route is authenticated and
  // the file lives outside anything the web server serves statically.
  const [photoView, setPhotoView] = useState({ open: false, url: null, punch: null, loading: false, error: '' });

  const openPunchPhoto = async (entryId, punch) => {
    setPhotoView({ open: true, url: null, punch, loading: true, error: '' });
    try {
      const res = await getPunchPhotoBlob(entryId, punch.id);
      setPhotoView({ open: true, url: URL.createObjectURL(res.data), punch, loading: false, error: '' });
    } catch (err) {
      setPhotoView({
        open: true, url: null, punch, loading: false,
        error: err.response?.status === 404
          ? 'The photo file is no longer on the server.'
          : 'Could not load the photo.',
      });
    }
  };

  const closePunchPhoto = () => {
    // Object URLs are held until revoked; without this every photo opened in a
    // session stays in memory for as long as the tab is open.
    setPhotoView((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, url: null, punch: null, loading: false, error: '' };
    });
  };

  // Bumped on every open and close, so a slow response for an entry the user
  // already closed (or moved past) never reopens or overwrites the dialog.
  const punchRequestRef = useRef(0);
  const closePunchDialog = () => {
    punchRequestRef.current += 1;
    setPunchDialog({ open: false, entry: null, punches: [], loading: false, error: null });
  };
  const openPunchDetails = async (entry) => {
    const requestId = ++punchRequestRef.current;
    setPunchDialog({ open: true, entry, punches: [], loading: true, error: null });
    try {
      const res = await getEntryPunches(entry.id);
      if (requestId !== punchRequestRef.current) return;
      setPunchDialog({ open: true, entry, punches: res.data?.punches || [], loading: false, error: null });
    } catch (err) {
      if (requestId !== punchRequestRef.current) return;
      setPunchDialog({ open: true, entry, punches: [], loading: false, error: punchLoadErrorMessage(err) });
    }
  };
  const [entryFormData, setEntryFormData] = useState({});
  const [entryFormErrors, setEntryFormErrors] = useState({});
  const [entryCompanies, setEntryCompanies] = useState([]);
  const [entryAffiliates, setEntryAffiliates] = useState([]);
  const [entryDepartments, setEntryDepartments] = useState([]);
  const [deptEnabled, setDeptEnabled] = useState(false);

  // State for payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentEntry, setPaymentEntry] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Invoice generation
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const canGenerateInvoice = hasPermission('can_manage_invoices');

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedEmployee === 'all') {
      loadAllEmployeesData();
    } else if (selectedEmployee) {
      loadEmployeeHours();
    } else {
      setHoursData(null);
      setAllEmployeesData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, filters]);

  const loadEmployees = async () => {
    try {
      const [res, shiftRes, presetsRes, reportsRes] = await Promise.all([
        getEmployees(),
        getShiftSettings().catch(() => ({ data: { time_format: '12h' } })),
        getPayPeriodPresets().catch(() => ({ data: [] })),
        needsDecisionScope ? getMyManagedEmployees().catch(() => ({ failed: true })) : Promise.resolve(null),
      ]);
      setEmployees(res.data);
      // A failed fetch leaves reviewIds null (not an empty set), so no "No managed
      // employees" notice shows and the server decides on each action.
      if (reportsRes?.failed) setError('Failed to load your direct reports list; decisions are checked by the server.');
      setReviewIds(needsDecisionScope && !reportsRes?.failed
        ? new Set((reportsRes?.data || []).map(x => x.employee_id).filter(id => id !== employeeId))
        : null);
      setPayPeriodPresets(buildPresetsFromApi(presetsRes.data));
      if (shiftRes.data?.time_format) {
        setTimeFormat(shiftRes.data.time_format);
      }
      // For non-admin/non-manager users, auto-select their employee
      if (!canSelectEmployee && employeeId) {
        setSelectedEmployee(employeeId);
      } else if (!canSelectEmployee && res.data.length > 0) {
        setSelectedEmployee(res.data[0].id);
      }
    } catch (err) {
      setError('Failed to load employees');
    }
  };

  const loadEmployeeHours = async () => {
    if (!selectedEmployee || selectedEmployee === 'all') return;
    
    setLoading(true);
    setError('');
    setAllEmployeesData(null);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.include_unpaid_before) params.include_unpaid_before = filters.include_unpaid_before;

      const res = await api.get(`/entries/employee/${selectedEmployee}/hours`, { params });
      setHoursData(res.data);
    } catch (err) {
      setError('Failed to load employee hours');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEmployeesData = async () => {
    setLoading(true);
    setError('');
    setHoursData(null);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.include_unpaid_before) params.include_unpaid_before = filters.include_unpaid_before;

      // Get summary stats and entries for all employees
      const [statsRes, entriesRes] = await Promise.all([
        getStatsSummary(params),
        getTimeEntries(params)
      ]);
      
      // Group entries by employee for summary
      const employeeSummary = {};
      entriesRes.data.forEach(entry => {
        const empKey = entry.employee_id;
        if (!employeeSummary[empKey]) {
          employeeSummary[empKey] = {
            employee_id: entry.employee_id,
            first_name: entry.first_name,
            last_name: entry.last_name,
            total_hours: 0,
            total_pay: 0,
            entry_count: 0,
            paid_count: 0,
            unpaid_count: 0
          };
        }
        employeeSummary[empKey].total_hours += parseFloat(entry.hours_worked || 0);
        if (employeeSummary[empKey].total_pay !== null) employeeSummary[empKey].total_pay += parseFloat(entry.total_pay || 0);
        if (entry.pay_hidden) employeeSummary[empKey].total_pay = null;
        employeeSummary[empKey].entry_count += 1;
        if (entry.is_paid) {
          employeeSummary[empKey].paid_count += 1;
        } else {
          employeeSummary[empKey].unpaid_count += 1;
        }
      });
      
      setAllEmployeesData({
        stats: statsRes.data,
        entries: entriesRes.data,
        employeeSummary: Object.values(employeeSummary).sort((a, b) => 
          `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
        )
      });
    } catch (err) {
      setError('Failed to load all employees data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    const preset = payPeriodPresets.find(p => p.value === value);
    if (preset) {
      const { start, end } = preset.getValue();
      setFilters({
        start_date: start,
        end_date: end,
        include_unpaid_before: preset.includeUnpaidPrevious && start ? start : '',
      });
    }
  };

  const handleSelectEntry = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(e => e !== id) 
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (!hoursData?.entries) return;
    const filtered = filteredSingleEntries;
    if (selectedEntries.length === filtered.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filtered.map(e => e.id));
    }
  };

  const handleBulkTogglePaid = async (isPaid) => {
    if (selectedEntries.length === 0) return;
    try {
      await markEntriesPaid(selectedEntries, isPaid);
      setSelectedEntries([]);
      loadEmployeeHours();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update paid status');
    }
  };

  const handleBulkApprove = async (status) => {
    if (selectedEntries.length === 0) return;
    try {
      await bulkApproveEntries(selectedEntries, status);
      setSelectedEntries([]);
      loadEmployeeHours();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update approval status');
    }
  };

  // Open payment dialog for a single entry
  const handleOpenPaymentDialog = (entry) => {
    setPaymentEntry(entry);
    setPaymentAmount(parseFloat(entry.paid_amount || 0).toFixed(2));
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentEntry(null);
    setPaymentAmount('');
  };

  const handleSavePayment = async () => {
    if (!paymentEntry) return;
    try {
      const amount = parseFloat(paymentAmount) || 0;
      const totalPay = parseFloat(paymentEntry.total_pay);
      const isPaid = amount >= totalPay;
      await markEntryPaid(paymentEntry.id, isPaid, amount);
      handleClosePaymentDialog();
      if (selectedEmployee === 'all') {
        loadAllEmployeesData();
      } else {
        loadEmployeeHours();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update payment');
    }
  };

  const handleGenerateInvoice = async () => {
    if (selectedEntries.length === 0) return;
    const entries = (hoursData?.entries || []).filter(e => selectedEntries.includes(e.id));
    // Validate: exclude already-invoiced
    const uninvoiced = entries.filter(e => !e.is_invoiced);
    if (uninvoiced.length === 0) {
      setError('All selected entries are already invoiced');
      return;
    }
    // Validate: must have company
    const noCompany = uninvoiced.filter(e => !e.company_id);
    if (noCompany.length > 0) {
      setError(`${noCompany.length} selected entries have no company assigned and cannot be invoiced`);
      return;
    }
    setGeneratingInvoice(true);
    try {
      const res = await generateInvoicesFromEntries(uninvoiced.map(e => e.id));
      const invoices = res.data.invoices || [];
      const msg = invoices.map(inv => `${inv.invoice_number} - ${inv.company_name}: ${formatCurrency(inv.total_amount)}`).join(', ');
      setError('');
      setSelectedEntries([]);
      loadEmployeeHours();
      if (onNavigate) {
        // Brief delay so the user sees the refresh, then navigate
        setTimeout(() => onNavigate('Invoices'), 500);
      } else {
        alert(`Invoice(s) created: ${msg}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate invoices');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Helper to get payment status chip (3 states: Paid, Partial, Unpaid)
  const getPaymentStatusChip = (entry, onClick, compact = false) => {
    if (entry.pay_hidden) return <StatusChip status={entry.is_paid ? 'paid' : 'unpaid'} title="Amounts hidden — needs the View pay rates permission" />;
    const totalPay = parseFloat(entry.total_pay || 0);
    const paidAmount = parseFloat(entry.paid_amount || 0);
    const interactive = !!onClick;

    if (paidAmount >= totalPay && totalPay > 0) {
      return (
        <StatusChip
          status="paid"
          title={interactive ? 'Fully paid - click to modify' : 'Fully paid'}
          onClick={onClick}
        />
      );
    } else if (paidAmount > 0) {
      return (
        <StatusChip
          status="partial"
          label={compact ? 'Partial' : `$${paidAmount.toFixed(2)}/$${totalPay.toFixed(2)}`}
          title={interactive ? `$${paidAmount.toFixed(2)} of $${totalPay.toFixed(2)} paid - click to modify` : `$${paidAmount.toFixed(2)} of $${totalPay.toFixed(2)} paid`}
          onClick={onClick}
        />
      );
    } else {
      return (
        <StatusChip
          status="unpaid"
          title={interactive ? 'Unpaid - click to record payment' : 'Unpaid'}
          onClick={onClick}
        />
      );
    }
  };

  // Toggle invoiced status for a single entry
  const handleToggleInvoiced = async (entry) => {
    try {
      await markEntryInvoiced(entry.id, !entry.is_invoiced);
      if (selectedEmployee === 'all') {
        loadAllEmployeesData();
      } else {
        loadEmployeeHours();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update invoiced status');
    }
  };

  // Helper to get invoiced status chip
  const getInvoicedStatusChip = (entry, onClick, compact = false) => {
    const interactive = !!onClick;

    if (entry.is_invoiced) {
      return (
        <StatusChip
          status="invoiced"
          title={interactive ? 'Invoiced - click to unmark' : 'Invoiced'}
          onClick={onClick}
        />
      );
    }
    return (
      <StatusChip
        status="uninvoiced"
        label={compact ? 'Uninvoiced' : 'Not Invoiced'}
        title={interactive ? 'Not invoiced - click to mark' : 'Not invoiced'}
        onClick={onClick}
      />
    );
  };

  const handleOpenEditNotes = (entry) => {
    setEditingEntry(entry);
    setManagerNotesInput(entry.manager_notes || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditNotes = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    setManagerNotesInput('');
  };

  const handleSaveManagerNotes = async () => {
    if (!editingEntry) return;
    setSavingNotes(true);
    try {
      await updateManagerNotes(editingEntry.id, managerNotesInput);
      if (selectedEmployee === 'all') {
        loadAllEmployeesData();
      } else {
        loadEmployeeHours();
      }
      handleCloseEditNotes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save manager notes');
    } finally {
      setSavingNotes(false);
    }
  };


  // Open edit entry dialog — loads companies/affiliates/departments on first open
  const handleOpenEditEntry = async (entry) => {
    // Lazy-load dropdown data on first use
    if (entryCompanies.length === 0) {
      try {
        const [cRes, aRes, dRes] = await Promise.all([
          getCompanies(),
          getAffiliates(),
          getDepartments(),
        ]);
        setEntryCompanies(cRes.data);
        setEntryAffiliates(aRes.data);
        setEntryDepartments(dRes.data || []);
        setDeptEnabled((dRes.data || []).length > 0);
      } catch (_) { /* proceed without dropdowns */ }
    }

    setEntryFormData({
      employee_id: entry.employee_id,
      company_id: entry.company_id || '',
      affiliate_id: entry.affiliate_id || '',
      department_id: entry.department_id || '',
      entry_date: (entry.entry_date || '').split('T')[0],
      start_time: entry.start_time,
      end_time: entry.end_time,
      break_minutes: entry.break_minutes,
      hourly_rate: entry.hourly_rate,
      description: entry.description || '',
      work_details: entry.work_details || '',
      manager_notes: entry.manager_notes || '',
      billable: entry.billable !== false,
      pay_basis: entry.pay_basis || '',
      _entryId: entry.id,
    });
    setEntryFormErrors({});
    setEntryDialogOpen(true);
  };

  const handleEntryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEntryFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmitEditEntry = async () => {
    const entryId = entryFormData._entryId;
    if (!entryId) return;
    try {
      const { _entryId, ...payload } = entryFormData;
      await updateTimeEntry(entryId, payload);
      setEntryDialogOpen(false);
      // Refresh data
      if (selectedEmployee === 'all') {
        loadAllEmployeesData();
      } else {
        loadEmployeeHours();
      }
    } catch (err) {
      setEntryFormErrors({ submit: err.response?.data?.error || 'Failed to update entry' });
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Combined filter helper (memoized)
  const filterEntries = useCallback((entries) => {
    let filtered = entries;
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(e => {
        if (e.pay_hidden) {
          if (paymentFilter === 'paid') return !!e.is_paid;
          if (paymentFilter === 'unpaid') return !e.is_paid;
          if (paymentFilter === 'partial') return false;
          return true;
        }
        const totalPay = parseFloat(e.total_pay || 0);
        const paidAmount = parseFloat(e.paid_amount || 0);
        if (paymentFilter === 'unpaid') return paidAmount === 0;
        if (paymentFilter === 'partial') return paidAmount > 0 && paidAmount < totalPay;
        if (paymentFilter === 'paid') return paidAmount >= totalPay && totalPay > 0;
        return true;
      });
    }
    if (companyFilter !== 'all') {
      if (companyFilter === 'none') {
        filtered = filtered.filter(e => !e.company_id);
      } else {
        filtered = filtered.filter(e => String(e.company_id) === companyFilter);
      }
    }
    if (approvalFilter !== 'all') {
      filtered = filtered.filter(e => e.approval_status === approvalFilter);
    }
    return filtered;
  }, [paymentFilter, companyFilter, approvalFilter]);

  // Memoize filtered entries for all-employees and single-employee views
  const filteredAllEntries = useMemo(
    () => filterEntries(allEmployeesData?.entries || []),
    [filterEntries, allEmployeesData?.entries]
  );
  const filteredSingleEntries = useMemo(
    () => filterEntries(hoursData?.entries || []),
    [filterEntries, hoursData?.entries]
  );

  // Unique companies from current entries for the company filter
  const availableCompanies = useMemo(() => {
    const entries = hoursData?.entries || allEmployeesData?.entries || [];
    const companies = {};
    entries.forEach(e => {
      if (e.company_id && e.company_name) {
        companies[e.company_id] = e.company_name;
      }
    });
    return Object.entries(companies).map(([id, name]) => ({ id, name }));
  }, [hoursData?.entries, allEmployeesData?.entries]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
          <Person sx={{ mr: 1 }} />
          {isAdmin ? 'Timesheets' : isManager ? 'Timesheets' : 'My Hours'}
          {needsDecisionScope && (
            <Tooltip title={DECIDE_TIP}>
              <InfoOutlined fontSize="small" color="action" sx={{ ml: 1, verticalAlign: 'middle' }} />
            </Tooltip>
          )}
        </Typography>
        <Tooltip title="Settings">
          <IconButton onClick={handleOpenSettings} size="small">
            <Settings />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Employee Selection */}
      <Paper sx={{ p: isMobile ? 1.5 : 1.5, mb: 2 }}>
        {/* Employee Dropdown - Always visible */}
        {canSelectEmployee && (
          <TextField
            select
            label="Select Employee"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            size="small"
            fullWidth={isMobile}
            InputLabelProps={{ shrink: true }}
            sx={{
              mb: 1.5,
              ...(isMobile ? {} : { minWidth: 220, maxWidth: 320 }),
            }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">-- Select Employee --</MenuItem>
            <MenuItem value="all" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              <Group sx={{ mr: 1, fontSize: 18, verticalAlign: 'middle' }} />
              All Employees (Summary)
            </MenuItem>
            <Divider />
            {employees.map(emp => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Mobile: Collapsible filter toggle */}
        {isMobile && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterList />}
            endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            sx={{ mb: showFilters ? 2 : 0 }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {(filters.start_date || filters.end_date) && (
              <Chip
                label="Active"
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </Button>
        )}

        {/* Filter Section - Collapsible on mobile */}
        <Collapse in={!isMobile || showFilters}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              select
              label="Pay Period"
              value={selectedPeriod}
              onChange={handlePeriodChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170, flex: isMobile ? '1 1 100%' : '0 0 auto' }}
            >
              {payPeriodPresets.map((preset) => (
                <MenuItem key={preset.value} value={preset.value} title={preset.description}>
                  {preset.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 160, flex: isMobile ? '1 1 48%' : '0 0 auto' }}
            />
            <TextField
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 160, flex: isMobile ? '1 1 48%' : '0 0 auto' }}
            />
          </Box>
        </Collapse>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {noReports && (
        <Alert severity="info" sx={{ mb: 2 }}>No managed employees assigned — contact an admin. You can view hours here, but approving and marking entries paid is only possible for your direct reports (never your own).</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* All Employees View */}
      {!loading && selectedEmployee === 'all' && allEmployeesData && (
        <>
          {/* Summary Stats Cards */}
          {(() => {
            const allUnpaid = anyPayHidden(allEmployeesData.entries) ? null : allEmployeesData.entries.reduce((sum, e) => {
              const totalPay = parseFloat(e.total_pay || 0);
              const paidAmount = parseFloat(e.paid_amount || 0);
              return sum + Math.max(0, totalPay - paidAmount);
            }, 0);
            return (
              <Grid container spacing={isMobile ? 1 : 3} sx={{ mb: 3 }}>
                {settings.showTotalEntries && (
                  <Grid item xs={6} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          Total Entries
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'}>
                          {allEmployeesData.stats?.total_entries || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {settings.showTotalHours && (
                  <Grid item xs={6} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          <AccessTime sx={{ fontSize: isMobile ? 12 : 14, mr: 0.5 }} />
                          Total Hours
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'}>
                          {formatHours(allEmployeesData.stats?.total_hours)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {settings.showTotalPay && (
                  <Grid item xs={6} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          <AttachMoney sx={{ fontSize: isMobile ? 12 : 14, mr: 0.5 }} />
                          Total Pay
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'}>
                          {formatPay(allEmployeesData.stats?.total_pay)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {settings.showAvgRate && (
                  <Grid item xs={6} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          Average Rate
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'}>
                          {formatPay(allEmployeesData.stats?.avg_rate)}/hr
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {settings.showTotalUnpaid && (
                  <Grid item xs={6} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#ffebee', height: '100%' }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                          <MoneyOff sx={{ fontSize: isMobile ? 12 : 14, mr: 0.5 }} />
                          Total Unpaid
                        </Typography>
                        <Typography variant={isMobile ? 'h5' : 'h4'} color={allUnpaid > 0 ? 'error' : 'inherit'}>
                          {formatPay(allUnpaid)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            );
          })()}

          {/* Employee Summary Table */}
          <Accordion defaultExpanded sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                <Group sx={{ mr: 1, verticalAlign: 'middle', fontSize: isMobile ? 18 : 24 }} />
                Employee Summary ({allEmployeesData.employeeSummary.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
              {/* Mobile Card View for Employee Summary */}
              {isMobile ? (
                <Stack spacing={1}>
                  {allEmployeesData.employeeSummary.map((emp) => (
                    <Card key={emp.employee_id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {emp.first_name} {emp.last_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip label={`${emp.paid_count} Paid`} color="success" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                          {emp.unpaid_count > 0 && (
                            <Chip label={`${emp.unpaid_count} Unpaid`} color="warning" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {emp.entry_count} entries • {formatHours(emp.total_hours)} hrs
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatPay(emp.total_pay)}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => setSelectedEmployee(emp.employee_id)}
                      >
                        View Details
                      </Button>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell align="right">Entries</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Total Pay</TableCell>
                        <TableCell align="center">Paid</TableCell>
                        <TableCell align="center">Unpaid</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allEmployeesData.employeeSummary.map((emp) => (
                        <TableRow key={emp.employee_id}>
                          <TableCell>{emp.first_name} {emp.last_name}</TableCell>
                          <TableCell align="right">{emp.entry_count}</TableCell>
                          <TableCell align="right">{formatHours(emp.total_hours)}</TableCell>
                          <TableCell align="right">{formatPay(emp.total_pay)}</TableCell>
                          <TableCell align="center">
                            <Chip label={emp.paid_count} color="success" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {emp.unpaid_count > 0 ? (
                              <Chip label={emp.unpaid_count} color="warning" size="small" />
                            ) : (
                              <Chip label="0" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedEmployee(emp.employee_id)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>

          {/* All Entries Preview */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                All Time Entries ({filteredAllEntries.length}{paymentFilter !== 'all' ? ` of ${allEmployeesData.entries.length}` : ''})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
              {/* Payment Status Filter */}
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Show:</Typography>
                {[
                  { value: 'all', label: 'All' },
                  { value: 'unpaid', label: 'Unpaid' },
                  { value: 'partial', label: 'Partial' },
                  { value: 'paid', label: 'Paid' },
                ].map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    color={paymentFilter === opt.value ? 'primary' : 'default'}
                    variant={paymentFilter === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setPaymentFilter(opt.value)}
                    clickable
                  />
                ))}
              </Box>
              {/* Mobile Card View */}
              {isMobile ? (
                <Stack spacing={1}>
                  {filteredAllEntries.slice(0, 50).map((entry) => (
                    <Card key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {entry.first_name} {entry.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(entry.entry_date).toLocaleDateString()} • {entry.company_name || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {canManagePaid && canDecide(entry) ? (
                            getPaymentStatusChip(entry, () => handleOpenPaymentDialog(entry), true)
                          ) : (
                            getPaymentStatusChip(entry, null, true)
                          )}
                          {canManagePaid && canDecide(entry) ? (
                            getInvoicedStatusChip(entry, () => handleToggleInvoiced(entry), true)
                          ) : (
                            getInvoicedStatusChip(entry, null, true)
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          {formatHours(entry.hours_worked)} hrs{entry.pay_basis === 'salary' ? '' : ` @ ${formatPay(entry.hourly_rate)}/hr`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {entry.pay_basis === 'salary' ? (
                            <Tooltip title="Covered by salary — billed to the client, not paid separately">
                              <Chip size="small" label="Salary" variant="outlined" />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {formatPay(entry.total_pay)}
                            </Typography>
                          )}
                          {canEditEntries && (
                            <Tooltip title="Edit Entry">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditEntry(entry)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEditManagerNotes && !canEditEntries && (
                            <Tooltip title="Edit Manager Notes">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditNotes(entry)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  ))}
                  {filteredAllEntries.length > 50 && (
                    <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 1 }}>
                      Showing 50 of {filteredAllEntries.length} entries. Use export to see all.
                    </Typography>
                  )}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Pay</TableCell>
                        <TableCell>Status</TableCell>
                        {(canEditEntries || canEditManagerNotes) && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAllEntries.slice(0, 50).map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{new Date(entry.entry_date).toLocaleDateString()}</TableCell>
                          <TableCell>{entry.first_name} {entry.last_name}</TableCell>
                          <TableCell>{entry.company_name || 'N/A'}</TableCell>
                          <TableCell align="right">{formatHours(entry.hours_worked)}</TableCell>
                          <TableCell align="right">{entry.pay_basis === 'salary' ? '—' : formatPay(entry.hourly_rate)}</TableCell>
                          <TableCell align="right">
                            {entry.pay_basis === 'salary' ? (
                              <Tooltip title="Covered by salary — billed to the client, not paid separately">
                                <Chip size="small" label="Salary" variant="outlined" />
                              </Tooltip>
                            ) : formatPay(entry.total_pay)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {canManagePaid && canDecide(entry) ? (
                                getPaymentStatusChip(entry, () => handleOpenPaymentDialog(entry))
                              ) : (
                                getPaymentStatusChip(entry, null)
                              )}
                              {canManagePaid && canDecide(entry) ? (
                                getInvoicedStatusChip(entry, () => handleToggleInvoiced(entry))
                              ) : (
                                getInvoicedStatusChip(entry, null)
                              )}
                            </Box>
                          </TableCell>
                          {(canEditEntries || canEditManagerNotes) && (
                            <TableCell align="center">
                              {canEditEntries && (
                                <Tooltip title="Edit Entry">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditEntry(entry)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canEditManagerNotes && !canEditEntries && (
                                <Tooltip title="Edit Manager Notes">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditNotes(entry)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      {filteredAllEntries.length > 50 && (
                        <TableRow>
                          <TableCell colSpan={(canEditEntries || canEditManagerNotes) ? 8 : 7} align="center">
                            <Typography variant="body2" color="textSecondary">
                              Showing 50 of {filteredAllEntries.length} entries. Use export to see all.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {/* Single Employee View */}
      {!loading && hoursData && selectedEmployee !== 'all' && (
        <>
          {/* Employee Info (condensed, toggleable) */}
          {settings.showEmployeeInfo && (
            <Paper sx={{ p: 1.5, mb: 1, bgcolor: '#f5f5f5', display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography variant="subtitle2">
                {hoursData.employee.first_name} {hoursData.employee.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">{hoursData.employee.email}</Typography>
              {hoursData.employee.hourly_rate != null && (
                <Typography variant="body2" color="textSecondary">${hoursData.employee.hourly_rate}/hr</Typography>
              )}
            </Paper>
          )}

          {/* Summary Strip moved into Unified Toolbar below */}

          {/* Weekly Breakdown */}
          {settings.showWeeklyBreakdown && Object.keys(hoursData.weeklyTotals).length > 0 && (
            <Accordion defaultExpanded={settings.defaultExpanded === 'weekly'} sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                  <CalendarMonth sx={{ mr: 1, verticalAlign: 'middle', fontSize: isMobile ? 18 : 24 }} />
                  Weekly Breakdown
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
                {isMobile ? (
                  <Stack spacing={1}>
                    {Object.entries(hoursData.weeklyTotals).map(([week, data]) => (
                      <Card key={week} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight="bold">{week}</Typography>
                          {data.hours > 40 ? (
                            <Chip label={`${formatHours(data.hours - 40)} OT`} color="error" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                          ) : (
                            <Chip label="Regular" color="success" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">
                            {formatHours(data.hours)} hrs • {data.entries.length} entries
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {formatPay(data.pay)}
                          </Typography>
                        </Box>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Week</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell align="right">Pay</TableCell>
                          <TableCell align="right">Entries</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(hoursData.weeklyTotals).map(([week, data]) => (
                          <TableRow key={week}>
                            <TableCell>{week}</TableCell>
                            <TableCell align="right">{formatHours(data.hours)}</TableCell>
                            <TableCell align="right">{formatPay(data.pay)}</TableCell>
                            <TableCell align="right">{data.entries.length}</TableCell>
                            <TableCell>
                              {data.hours > 40 ? (
                                <Chip label={`${formatHours(data.hours - 40)} OT`} color="error" size="small" />
                              ) : (
                                <Chip label="Regular" color="success" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Daily Breakdown */}
          {settings.showDailyBreakdown && Object.keys(hoursData.dailyTotals).length > 0 && (
            <Accordion defaultExpanded={settings.defaultExpanded === 'daily'} sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                  Daily Breakdown ({Object.keys(hoursData.dailyTotals).length} days)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
                {isMobile ? (
                  <Stack spacing={1}>
                    {Object.entries(hoursData.dailyTotals)
                      .sort(([a], [b]) => new Date(b) - new Date(a))
                      .map(([date, data]) => {
                        const d = new Date(date);
                        return (
                          <Card key={date} variant="outlined" sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {dayNames[d.getDay()]}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {d.toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatHours(data.hours)} hrs
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  {formatPay(data.pay)}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                  </Stack>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Day</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell align="right">Pay</TableCell>
                          <TableCell align="right">Entries</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(hoursData.dailyTotals)
                          .sort(([a], [b]) => new Date(b) - new Date(a))
                          .map(([date, data]) => {
                            const d = new Date(date);
                            return (
                              <TableRow key={date}>
                                <TableCell>{d.toLocaleDateString()}</TableCell>
                                <TableCell>{dayNames[d.getDay()]}</TableCell>
                                <TableCell align="right">{formatHours(data.hours)}</TableCell>
                                <TableCell align="right">{formatPay(data.pay)}</TableCell>
                                <TableCell align="right">{data.entries.length}</TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Unified Toolbar: Period Totals + Filters */}
          <Paper sx={{ p: 1, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {/* Period Totals — leading section */}
            {settings.showSummaryStrip && (() => {
              const singleUnpaid = anyPayHidden(hoursData.entries || []) ? null : (hoursData.entries || []).reduce((sum, e) => {
                const totalPay = parseFloat(e.total_pay || 0);
                const paidAmount = parseFloat(e.paid_amount || 0);
                return sum + Math.max(0, totalPay - paidAmount);
              }, 0);
              return (
                <>
                  <IconButton size="small" onClick={() => setSummaryExpanded(!summaryExpanded)} sx={{ p: 0.25 }}>
                    {summaryExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                  {summaryExpanded && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 0.75 : 1.25, alignItems: 'center', mr: 1, pr: 1, borderRight: isMobile ? 'none' : '1px solid', borderColor: 'divider', fontSize: '0.75rem' }}>
                      {settings.showTotalEntries && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{hoursData.summary.total_entries}</strong> entries</Typography>
                      )}
                      {settings.showTotalHours && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.total_hours)}</strong> hrs</Typography>
                      )}
                      {settings.showRegularHours && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.regular_hours)}</strong> reg</Typography>
                      )}
                      {hoursData.summary.salary_covered_hours > 0 && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.salary_covered_hours)}</strong> salary-covered</Typography>
                      )}
                      {settings.showOvertimeHours && hoursData.summary.overtime_hours > 0 && (
                        <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.overtime_hours)}</strong> OT</Typography>
                      )}
                      {settings.showTotalPay && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatPay(hoursData.summary.total_pay)}</strong> total</Typography>
                      )}
                      {settings.showAvgRate && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatPay(hoursData.summary.avg_rate)}</strong> avg</Typography>
                      )}
                      {settings.showAvgPerEntry && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}><strong>{formatHours(hoursData.summary.avg_hours_per_entry)}</strong>/entry</Typography>
                      )}
                      {settings.showTotalUnpaid && (singleUnpaid === null || singleUnpaid > 0) && (
                        <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}><MoneyOff sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} /><strong>{formatPay(singleUnpaid)}</strong> unpaid</Typography>
                      )}
                    </Box>
                  )}
                </>
              );
            })()}
            {/* Filters — compact dropdowns */}
            {(() => {
              const filterSx = {
                minWidth: 120,
                '& .MuiInputBase-root': { fontSize: '0.75rem' },
                '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                '& .MuiSelect-select': { py: 0.5 },
              };
              const filterLabelProps = { shrink: true };
              return (
                <>
                  <TextField
                    select
                    size="small"
                    label="Payment"
                    value={paymentFilter}
                    onChange={(e) => { setPaymentFilter(e.target.value); setSelectedEntries([]); }}
                    InputLabelProps={filterLabelProps}
                    sx={filterSx}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                  </TextField>
                  {approvalEnabled && (
                    <TextField
                      select
                      size="small"
                      label="Approval"
                      value={approvalFilter}
                      onChange={(e) => { setApprovalFilter(e.target.value); setSelectedEntries([]); }}
                      InputLabelProps={filterLabelProps}
                      sx={filterSx}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                    </TextField>
                  )}
                  {availableCompanies.length > 0 && (
                    <TextField
                      select
                      size="small"
                      label="Client"
                      value={companyFilter}
                      onChange={(e) => { setCompanyFilter(e.target.value); setSelectedEntries([]); }}
                      InputLabelProps={filterLabelProps}
                      sx={{ ...filterSx, minWidth: 160 }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                      {availableCompanies.map(c => (
                        <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </>
              );
            })()}
          </Paper>

          {/* Selected Total + Bulk Actions */}
          {selectedEntries.length > 0 && (canManagePaid || hasPermission('can_approve_time') || hasPermission('can_manage_invoices')) && (() => {
            const selEntries = (hoursData?.entries || []).filter(e => selectedEntries.includes(e.id));
            const selHours = selEntries.reduce((s, e) => s + parseFloat(e.hours_worked || 0), 0);
            const selHidden = anyPayHidden(selEntries);
            const selPay = selHidden ? null : selEntries.reduce((s, e) => s + parseFloat(e.total_pay || 0), 0);
            const selUnpaid = selHidden ? null : selEntries.reduce((s, e) => s + Math.max(0, parseFloat(e.total_pay || 0) - parseFloat(e.paid_amount || 0)), 0);
            const selUninvoiced = selEntries.filter(e => !e.is_invoiced).length;
            const selDecidable = selEntries.length > 0 && selEntries.every(canDecide);
            return (
              <Paper elevation={2} sx={{ mb: 2, p: isMobile ? 1 : 1.25, bgcolor: '#e3f2fd', border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: isMobile ? 1 : 2, mb: 1 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    Selected — {selectedEntries.length}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>{formatHours(selHours)}</strong> hrs
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>{formatPay(selPay)}</strong> total
                  </Typography>
                  <Typography variant="caption" color={selUnpaid > 0 ? 'error.main' : 'text.secondary'} sx={{ fontSize: '0.75rem' }}>
                    <strong>{formatPay(selUnpaid)}</strong> unpaid
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {approvalEnabled && hasPermission('can_approve_time') && (
                    <Tooltip title={selDecidable ? '' : DECIDE_TIP}><span><Button
                      disabled={!selDecidable}
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={() => handleBulkApprove('approved')}
                    >
                      Approve
                    </Button></span></Tooltip>
                  )}
                  {approvalEnabled && hasPermission('can_approve_time') && (
                    <Tooltip title={selDecidable ? '' : DECIDE_TIP}><span><Button
                      disabled={!selDecidable}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleBulkApprove('pending')}
                    >
                      Unapprove
                    </Button></span></Tooltip>
                  )}
                  {canManagePaid && (
                    <Tooltip title={selDecidable ? '' : DECIDE_TIP}><span><Button
                      disabled={!selDecidable}
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={!isMobile && <CheckCircle />}
                      onClick={() => handleBulkTogglePaid(true)}
                    >
                      {isMobile ? 'Paid' : 'Mark Paid'}
                    </Button></span></Tooltip>
                  )}
                  {canManagePaid && (
                    <Tooltip title={selDecidable ? '' : DECIDE_TIP}><span><Button
                      disabled={!selDecidable}
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={!isMobile && <Cancel />}
                      onClick={() => handleBulkTogglePaid(false)}
                    >
                      {isMobile ? 'Unpaid' : 'Mark Unpaid'}
                    </Button></span></Tooltip>
                  )}
                  {canGenerateInvoice && selUninvoiced > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={!isMobile && <Receipt />}
                      onClick={handleGenerateInvoice}
                      disabled={generatingInvoice}
                    >
                      {generatingInvoice ? 'Creating...' : isMobile ? 'Invoice' : `Generate Invoice (${selUninvoiced})`}
                    </Button>
                  )}
                </Box>
              </Paper>
            );
          })()}

          {/* Entries count */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {filteredSingleEntries.length} entries{(paymentFilter !== 'all' || companyFilter !== 'all' || approvalFilter !== 'all') ? ` (filtered from ${hoursData.entries.length})` : ''}
          </Typography>
          {/* Scrollable entries container */}
          <Box sx={{ maxHeight: isMobile ? 'calc(100vh - 320px)' : 'calc(100vh - 280px)', overflow: 'auto' }}>
              {/* Mobile Card View */}
              {isMobile ? (
                <Stack spacing={1}>
                  {/* Select All for Mobile */}
                  {canBulkSelect && filteredSingleEntries.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            indeterminate={selectedEntries.length > 0 && selectedEntries.length < filteredSingleEntries.length}
                            checked={filteredSingleEntries.length > 0 && selectedEntries.length === filteredSingleEntries.length}
                            onChange={handleSelectAll}
                          />
                        }
                        label={<Typography variant="body2">Select All</Typography>}
                      />
                    </Box>
                  )}
                  {filteredSingleEntries.map((entry) => {
                    const d = new Date(entry.entry_date);
                    return (
                      <Card 
                        key={entry.id} 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5,
                          bgcolor: selectedEntries.includes(entry.id) ? 'action.selected' : 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {canBulkSelect && (
                              <Checkbox
                                size="small"
                                checked={selectedEntries.includes(entry.id)}
                                onChange={() => handleSelectEntry(entry.id)}
                                sx={{ p: 0 }}
                              />
                            )}
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {dayNames[d.getDay()]} {d.toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatTimeDisplay(entry.start_time, timeFormat)} - {formatTimeDisplay(entry.end_time, timeFormat)} ({entry.break_minutes}m break)
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {approvalEnabled && (
                              <StatusChip status={entry.approval_status === 'approved' ? 'approved' : 'pending'} />
                            )}
                            {canManagePaid && canDecide(entry) ? (
                              getPaymentStatusChip(entry, () => handleOpenPaymentDialog(entry), true)
                            ) : (
                              getPaymentStatusChip(entry, null, true)
                            )}
                            {canManagePaid && canDecide(entry) ? (
                              getInvoicedStatusChip(entry, () => handleToggleInvoiced(entry), true)
                            ) : (
                              getInvoicedStatusChip(entry, null, true)
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: entry.description || entry.work_details || entry.company_name ? 1 : 0 }}>
                          <Typography variant="body2">
                            {formatHours(entry.hours_worked)} hrs{entry.pay_basis === 'salary' ? '' : ` @ ${formatPay(entry.hourly_rate)}/hr`}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entry.pay_basis === 'salary' ? (
                              <Tooltip title="Covered by salary — billed to the client, not paid separately"><Chip size="small" label="Salary" variant="outlined" /></Tooltip>
                            ) : (
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {formatPay(entry.total_pay)}
                              </Typography>
                            )}
                            {canEditManagerNotes && (
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenEditNotes(entry)}
                                sx={{ p: 0.5 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        {(entry.company_name || entry.description || entry.work_details) && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {entry.company_name && `Company: ${entry.company_name}`}
                            {entry.company_name && entry.description && ' • '}
                            {entry.description && entry.description}
                            {(entry.company_name || entry.description) && entry.work_details && ' • '}
                            {entry.work_details && `Details: ${entry.work_details}`}
                          </Typography>
                        )}
                      </Card>
                    );
                  })}
                  {filteredSingleEntries.length === 0 && (
                    <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                      {paymentFilter !== 'all' ? `No ${paymentFilter} entries found` : 'No time entries found for selected period'}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {canBulkSelect && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedEntries.length > 0 && selectedEntries.length < filteredSingleEntries.length}
                              checked={filteredSingleEntries.length > 0 && selectedEntries.length === filteredSingleEntries.length}
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                        )}
                        <TableCell>Date</TableCell>
                        <TableCell>Day</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell align="right">Break</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Pay</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Punch</TableCell>
                        {(canEditEntries || canEditManagerNotes) && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSingleEntries.map((entry) => {
                        const d = new Date(entry.entry_date);
                        return (
                          <TableRow key={entry.id} selected={selectedEntries.includes(entry.id)}>
                            {canBulkSelect && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedEntries.includes(entry.id)}
                                  onChange={() => handleSelectEntry(entry.id)}
                                />
                              </TableCell>
                            )}
                            <TableCell>{d.toLocaleDateString()}</TableCell>
                            <TableCell>{dayNames[d.getDay()]}</TableCell>
                            <TableCell>{formatTimeDisplay(entry.start_time, timeFormat)}</TableCell>
                            <TableCell>{formatTimeDisplay(entry.end_time, timeFormat)}</TableCell>
                            <TableCell align="right">{entry.break_minutes} min</TableCell>
                            <TableCell align="right">{formatHours(entry.hours_worked)}</TableCell>
                            <TableCell align="right">{entry.pay_basis === 'salary' ? '—' : formatPay(entry.hourly_rate)}</TableCell>
                            <TableCell align="right">{entry.pay_basis === 'salary' ? (
                              <Tooltip title="Covered by salary — billed to the client, not paid separately"><Chip size="small" label="Salary" variant="outlined" /></Tooltip>
                            ) : formatPay(entry.total_pay)}</TableCell>
                            <TableCell>{entry.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {approvalEnabled && (
                                  <StatusChip status={entry.approval_status === 'approved' ? 'approved' : 'pending'} />
                                )}
                                {canManagePaid && canDecide(entry) ? (
                                  getPaymentStatusChip(entry, () => handleOpenPaymentDialog(entry))
                                ) : (
                                  getPaymentStatusChip(entry, null)
                                )}
                                {canManagePaid && canDecide(entry) ? (
                                  getInvoicedStatusChip(entry, () => handleToggleInvoiced(entry))
                                ) : (
                                  getInvoicedStatusChip(entry, null)
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              {/* Same shape as My Time Entries: description
                                  is the primary text; work_details and
                                  manager_notes surface as small chips you can
                                  hover for the full content. Keeps the row
                                  compact even when an entry has notes on
                                  multiple tiers. */}
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                {entry.description && (
                                  <Tooltip title={entry.description}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                      {entry.description}
                                    </Typography>
                                  </Tooltip>
                                )}
                                {entry.work_details && (
                                  <Tooltip title={entry.work_details}>
                                    <Chip size="small" label="Details" variant="outlined" />
                                  </Tooltip>
                                )}
                                {canEditManagerNotes && entry.manager_notes && (
                                  <Tooltip title={`Manager: ${entry.manager_notes}`}>
                                    <Chip size="small" label="Mgr" color="warning" />
                                  </Tooltip>
                                )}
                                {!entry.description && !entry.work_details && !(canEditManagerNotes && entry.manager_notes) && (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {entry.entry_source === 'punch' ? (
                                <Tooltip title="Punch details — where it was clocked, on whose network, which device, and the photo">
                                  <IconButton size="small" onClick={() => openPunchDetails(entry)} aria-label="View punch details">
                                    <FactCheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title={entrySourceLabel(entry.entry_source).tooltip}>
                                  <Typography variant="caption" color="text.disabled">{entrySourceLabel(entry.entry_source).label}</Typography>
                                </Tooltip>
                              )}
                            </TableCell>
                            {(canEditEntries || canEditManagerNotes) && (
                              <TableCell align="center">
                                {canEditEntries && (
                                  <Tooltip title="Edit Entry">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenEditEntry(entry)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canEditManagerNotes && !canEditEntries && (
                                  <Tooltip title="Edit Manager Notes">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenEditNotes(entry)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {filteredSingleEntries.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11 + (canBulkSelect ? 1 : 0) + ((canEditEntries || canEditManagerNotes) ? 1 : 0)} align="center">
                            {paymentFilter !== 'all' ? `No ${paymentFilter} entries found` : 'No time entries found for selected period'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
          </Box>
        </>
      )}

      {!selectedEmployee && (
        <Paper sx={{ p: isMobile ? 3 : 4, textAlign: 'center' }}>
          <Person sx={{ fontSize: isMobile ? 48 : 64, color: '#ccc', mb: 2 }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="textSecondary">
            Select an employee to view their hours
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            Choose an employee from the dropdown above to see detailed time tracking information
          </Typography>
          {canSelectEmployee && (
            <Typography variant="body2" color="primary" sx={{ mt: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
              Or select "All Employees" to see a summary across all employees
            </Typography>
          )}
        </Paper>
      )}

      <ManagerNotesDialog
        open={editDialogOpen}
        onClose={handleCloseEditNotes}
        onSave={handleSaveManagerNotes}
        editingEntry={editingEntry}
        managerNotesInput={managerNotesInput}
        setManagerNotesInput={setManagerNotesInput}
        savingNotes={savingNotes}
        timeFormat={timeFormat}
      />

      <HoursSettingsDialog
        open={settingsOpen}
        onCancel={handleCancelSettings}
        onSave={handleSaveSettings}
        tempSettings={tempSettings}
        setTempSettings={setTempSettings}
      />

      <EntryPaymentDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        onSave={handleSavePayment}
        paymentEntry={paymentEntry}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
      />

      <Dialog
        open={punchDialog.open}
        onClose={closePunchDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Punch details
          <Typography variant="body2" color="text.secondary">
            {punchDialog.entry
              ? `${punchDialog.entry.first_name || ''} ${punchDialog.entry.last_name || ''} · ${new Date(punchDialog.entry.entry_date).toLocaleDateString()}`
              : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            How this entry was captured. Separate from Description and Work Details,
            which are the employee&apos;s own account of what they did.
          </Alert>
          {punchDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : punchDialog.error ? (
            <Alert
              severity={punchDialog.error.severity}
              sx={{ mb: 1 }}
              action={punchDialog.error.severity === 'error' ? (
                <Button color="inherit" size="small" onClick={() => openPunchDetails(punchDialog.entry)}>Try again</Button>
              ) : undefined}
            >
              {punchDialog.error.message}
            </Alert>
          ) : punchDialog.punches.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {emptyPunchTrailMessage(punchDialog.entry?.entry_source)}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Where</TableCell>
                  <TableCell>Network</TableCell>
                  <TableCell align="center">In fence</TableCell>
                  <TableCell>How</TableCell>
                  <TableCell align="center">Photo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {punchDialog.punches.map((p) => {
                  const label = { clock_in: 'Clocked in', break_start: 'Break started', break_end: 'Break ended', clock_out: 'Clocked out' };
                  const yesNo = (v) => (v === null || v === undefined ? '—' : v ? 'Yes' : 'No');
                  return (
                    <TableRow key={p.id}>
                      <TableCell>{label[p.punch_type] || p.punch_type}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(p.punch_time).toLocaleString()}
                        {p.source === 'offline_sync' && offlineTimeNotHonoured(p) && (
                          <Typography variant="caption" display="block" color="error">
                            Device time {offlineTimeNotHonoured(p).deviceTime} not used - {offlineTimeNotHonoured(p).reason}
                            <Tooltip title="This punch was queued offline but arrived without a valid sync token (older app, token older than 72 hours, many punches in one offline stretch, or one that could not be verified), so it was recorded at the time it reached the server, shown above. Confirm the actual time with the employee and correct the time entry (Edit Entry: Start Time / End Time) before payroll. A sync-token verification flag alone does not mean the employee did anything wrong; common causes are an app update, a server key change, a slow phone clock, or a long time offline.">
                              <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                            </Tooltip>
                          </Typography>
                        )}
                        {p.source === 'offline_sync' && p.received_at && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Recorded offline, synced {new Date(p.received_at).toLocaleString()}
                            <Tooltip title="This punch was made on the employee's device without a connection. The time shown is when they actually punched; it synced to the server at the time shown here.">
                              <InfoOutlined sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />
                            </Tooltip>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.work_site_name || '—'}
                        {p.latitude != null && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                            {p.gps_accuracy_meters != null && ` ±${Math.round(p.gps_accuracy_meters)}m`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {String(p.ip_address || '—')}
                        <Typography variant="caption" display="block" color="text.secondary">
                          on network: {yesNo(p.ip_check_passed)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{yesNo(p.geo_check_passed)}</TableCell>
                      <TableCell>
                        {p.source}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {p.verification_method || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {p.photo_url ? (
                          <Tooltip title="View the photo taken at this punch">
                            <IconButton
                              size="small"
                              color="success"
                              aria-label="View punch photo"
                              onClick={() => openPunchPhoto(punchDialog.entry?.id, p)}
                            >
                              <PhotoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="No photo — this punch was not made at a kiosk">
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePunchDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={photoView.open} onClose={closePunchPhoto} maxWidth="sm" fullWidth>
        <DialogTitle>
          Punch photo
          {photoView.punch && (
            <Typography variant="body2" color="text.secondary">
              {photoView.punch.first_name} {photoView.punch.last_name} — {new Date(photoView.punch.punch_time).toLocaleString()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {photoView.loading && <CircularProgress sx={{ my: 4 }} />}
          {photoView.error && <Alert severity="warning" sx={{ my: 2 }}>{photoView.error}</Alert>}
          {photoView.url && (
            <Box
              component="img"
              src={photoView.url}
              alt="Photo taken at this punch"
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePunchPhoto}>Close</Button>
        </DialogActions>
      </Dialog>

      <TimeEntryFormDialog
        open={entryDialogOpen}
        onClose={() => setEntryDialogOpen(false)}
        onSubmit={handleSubmitEditEntry}
        editMode={true}
        formData={entryFormData}
        formErrors={entryFormErrors}
        handleInputChange={handleEntryInputChange}
        setFormData={setEntryFormData}
        canFilterEmployees={false}
        employees={employees}
        companies={entryCompanies}
        affiliates={entryAffiliates}
        departments={entryDepartments}
        departmentsEnabled={deptEnabled}
        isAdmin={isAdmin}
        canManagePaid={canManagePaid}
        canEditTimes={canEditEntries}
      />
    </Box>
  );
}
